import { AgentMlsIds } from "model/agent-mls-ids";
import { isNone } from "shared/agent-mls-ids";

// The fixes for MLS IDs that aren't aligned, each as the calls that make it, in order. The MLS ID endpoints only take an
// aligned value, so a fix is plain calls on the real records: an AgentBoard row added or deleted, and a Featured row
// (slot) set to "None". Featured rows clear first and profile rows go after, and a fix that ends aligned may
// finish with the MLS ID DELETE.
export type FixStep =
	// PUT /client-board/{id}.json with both IDs "None". The row stays.
	| { kind: "rowToNone"; clientBoardId: number }
	// DELETE /agent-board/{id}.json: one AgentBoard row, whatever its value.
	| { kind: "deleteRow"; entryId: number }
	// POST /agent-boards.json: the value on the agent's profile (Assign).
	| { kind: "addAgentBoard"; agentId: number; boardId: number; mlsId: string }
	// DELETE /agent-mls-id/{id}.json: an aligned value's row, with one of its slots.
	| { kind: "delete"; entryId: number };

const entryOf = (agentMlsIds: AgentMlsIds, entryId: number) => agentMlsIds.entries.find((e) => e.id === entryId) ?? null;

// Remove on the profile's MLS IDs that need review: delete that one row. That's a stray, or on an
// office-sourced account any of an agent's MLS IDs, since nothing there is paired with Featured Listings.
export const deleteStraySteps = (agentMlsIds: AgentMlsIds, entryId: number): FixStep[] | null => {
	const entry = entryOf(agentMlsIds, entryId);
	const removable = !!entry && (agentMlsIds.officeAccount || agentMlsIds.clientBoardLinks.get(entryId)?.state === "stray");
	return removable ? [{ kind: "deleteRow", entryId }] : null;
};

// The one row that holds a value on more slots than that, for Remove Duplicate.
const soleHolder = (agentMlsIds: AgentMlsIds, entryId: number) => {
	const entry = entryOf(agentMlsIds, entryId);
	return entry && entry.needsSlot && entry.holderCount === 1 && entry.clientBoardIds.length > 1 ? entry : null;
};

// Remove Duplicate, Keep Selected: every other slot of the value goes to "None".
export const keepOneSteps = (agentMlsIds: AgentMlsIds, entryId: number, keepRowId: number): FixStep[] | null => {
	const entry = soleHolder(agentMlsIds, entryId);
	if (!entry || !entry.clientBoardIds.includes(keepRowId)) {
		return null;
	}
	return entry.clientBoardIds.filter((id) => id !== keepRowId).sort((a, b) => a - b).map((clientBoardId) => ({ kind: "rowToNone", clientBoardId }));
};

// Remove Duplicate, Remove All: every slot but one goes to "None", and then the value, now aligned, is cleared from the
// profile with its last slot. Null for a value another agent also has: that's a Review of each row instead.
export const removeDuplicateSteps = (agentMlsIds: AgentMlsIds, entryId: number): FixStep[] | null => {
	const entry = soleHolder(agentMlsIds, entryId);
	if (!entry || entry.otherAgentIds.length) {
		return null;
	}
	const rows = entry.clientBoardIds.slice().sort((a, b) => a - b);
	return [...rows.slice(1).map((clientBoardId): FixStep => ({ kind: "rowToNone", clientBoardId })), { kind: "delete", entryId: entry.id }];
};

// Review, Assign. On a Shared row the chosen agent keeps the value, or is given it when they have no MLS ID on the
// board, and every other agent's rows for it are deleted. The row is added first, so a run that stops partway never
// leaves the value on no profile. On an orphan (Unknown, Not Billable or a Duplicate's extra slot) the value goes on the
// chosen agent's profile, which gives the slot a holder. The chosen agent must be billable. Null when the row or agent
// no longer fits.
export const assignSteps = (agentMlsIds: AgentMlsIds, clientBoardId: number, agentId: number): FixStep[] | null => {
	const row = agentMlsIds.rows.get(clientBoardId);
	if (!row || isNone(row.agentId) || !agentMlsIds.agents.get(agentId)?.billable || agentMlsIds.holderOf.has(clientBoardId)) {
		return null;
	}
	const withValue = agentMlsIds.entries.filter((e) => e.clientBoardIds.includes(clientBoardId));
	if (agentMlsIds.sharedRows.has(clientBoardId)) {
		const holds = withValue.some((e) => e.needsSlot && e.agentId === agentId);
		if (!holds && agentMlsIds.entries.some((e) => e.agentId === agentId && e.boardId === row.boardId)) {
			return null;
		}
		const deletes = withValue.filter((e) => e.agentId !== agentId).map((e): FixStep => ({ kind: "deleteRow", entryId: e.id }));
		return holds ? deletes : [{ kind: "addAgentBoard", agentId, boardId: row.boardId, mlsId: row.agentId }, ...deletes];
	}
	return [{ kind: "addAgentBoard", agentId, boardId: row.boardId, mlsId: row.agentId }];
};
